import { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { SearchParam } from '@common/ts/enums';
import { ArrowLeftRight, Plus, Filter, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import useTranslation from '@/hooks/useTranslation';
import useGlobalProgressStore from '@/store/GlobalProgressStore';
import { useGetFilters } from '@/reactQuery/get';
import { useListComparisons, useCompareSegments } from '@/reactQuery/comparison';
import { useMutateReRunAnalyze } from '@/reactQuery/post';
import axiosInstanceAnalyze from '@/axios/AxiosInstanceAnalyze';
import { API_ENDPOINTS } from '@/api-endpoints';
import EditFilterModal from '@/components/Modals/FilterModal/EditFilterModal';
import ComparisonCard from './ComparisonCard';
import CompareFiltersModal from './CompareFiltersModal';

/** Ticking elapsed timer that updates every second. */
function ElapsedTimer({ startedAt }: { startedAt: number }) {
  const [elapsed, setElapsed] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    intervalRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [startedAt]);

  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;

  return (
    <p className="text-xs text-neutral-400 mt-1 tabular-nums">
      {mins}:{secs.toString().padStart(2, '0')}
    </p>
  );
}

interface ComparisonHubProps {
  analysisLanguage: string;
  // TELV1-312 — `force` is forwarded unchanged from CompareTab; the
  // banner inside CompareTab uses it. ComparisonHub's own re-run call
  // (line ~218) already hardcodes force: true via the mutation hook,
  // so this prop's `force` param is only for downstream pass-through.
  onReRun?: (filterId?: string | null, force?: boolean) => Promise<void>;
  isRerunLoading?: boolean;
}

function ComparisonHub({ analysisLanguage, onReRun, isRerunLoading }: ComparisonHubProps) {
  const { lang } = useTranslation();
  const { project_id: projectId } = useParams();
  const [, setSearchParams] = useSearchParams();
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [showCreateFilterModal, setShowCreateFilterModal] = useState(false);
  const [generatingProgress, setGeneratingProgress] = useState('');
  const queryClient = useQueryClient();

  // useGetFilters is typed as FiltersDataToSendProps[] (the create-filter
  // request shape), but the API actually returns each row with `_id` and
  // `name` fields. The existing convention (see DownloadAnalysisButton.tsx)
  // is to cast at the consumer site rather than widen the request type.
  const {
    data: filtersRaw,
    isLoading: isLoadingFilters,
    refetch: refetchFilters,
  } = useGetFilters(projectId);
  const filters = filtersRaw as unknown as Array<{ _id: string; name: string }> | undefined;
  const { data: comparisons, isLoading: isLoadingComparisons } = useListComparisons(projectId);
  const {
    mutateAsync: compareSegments,
    isPending: isComparing,
    abort: abortComparison,
  } = useCompareSegments();
  const { mutateAsync: reRunAnalyze } = useMutateReRunAnalyze();
  const [comparisonStartedAt, setComparisonStartedAt] = useState<number | null>(null);

  // Set when the backend returns RA_ANALYSIS_OUTDATED for a comparison attempt.
  // Holds the per-filter ids that need refreshing + the original compare attempt
  // so we can re-trigger it once the refresh completes.
  const [outdatedRefresh, setOutdatedRefresh] = useState<{
    staleFilterIds: string[];
    filterIdA: string;
    filterIdB: string;
  } | null>(null);
  const [refreshStatus, setRefreshStatus] = useState<string>('');
  const isRefreshing = !!refreshStatus;

  const hasEnoughFilters = (filters?.length ?? 0) >= 2;

  const handleCompare = async (filterIdA: string, filterIdB: string) => {
    setShowCompareModal(false);
    setComparisonStartedAt(Date.now());
    setGeneratingProgress(lang.get('msg.comparisonStarting'));
    const progressId = `comparison-${filterIdA}-${filterIdB}`;
    useGlobalProgressStore.getState().addEntry({
      id: progressId,
      type: 'comparison',
      label: lang.get('msg.comparisonGenerating'),
      startedAt: Date.now(),
      projectId: projectId!,
      returnUrl: `${window.location.pathname}${window.location.search}`,
    });
    try {
      const { comparisonId } = await compareSegments({
        projectId: projectId!,
        filterIdA,
        filterIdB,
        onProgress: setGeneratingProgress,
      });
      setGeneratingProgress('');
      setComparisonStartedAt(null);
      useGlobalProgressStore.getState().removeEntry(progressId);
      // Navigate to the report
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set(SearchParam.COMPARISON_ID, comparisonId);
        return next;
      });
    } catch (error: unknown) {
      setGeneratingProgress('');
      setComparisonStartedAt(null);
      useGlobalProgressStore.getState().removeEntry(progressId);
      // AxiosInstanceAnalyze rejects with `error.response` (not the AxiosError
      // itself — see its interceptor's `Promise.reject(error.response)`), so
      // the rejection value is the response object with `.status` / `.data`
      // at the top level. We also accept the bare-AxiosError shape so this
      // works if the interceptor ever stops unwrapping.
      const rawError = error as {
        status?: number;
        data?: { code?: string; message?: string; stale_filter_ids?: string[] };
        response?: {
          status?: number;
          data?: { code?: string; message?: string; stale_filter_ids?: string[] };
        };
        message?: string;
      };
      const status = rawError.response?.status ?? rawError.status;
      const data = rawError.response?.data ?? rawError.data;
      // NOTE: Comparison REST endpoint uses 'RA_ANALYSIS_OUTDATED' (separate contract).
      // The Research Assistant SSE channel uses 'ANALYSIS_OUTDATED'. Do NOT couple.
      // See D.2 spec + backend/apps/analyzer/src/comparison/comparison.controller.ts for the symmetric comment.
      if (data?.code === 'SEGMENT_EMPTY') {
        toast.error(lang.get('msg.comparisonSegmentEmpty'));
      } else if (data?.code === 'ANALYSIS_IN_PROGRESS') {
        toast.error(lang.get('msg.analysisInProgressCompare'));
      } else if (data?.code === 'RA_ANALYSIS_OUTDATED') {
        // Surface the affected filters and the original compare attempt so
        // the user can refresh in-place and we can auto-retry the compare.
        // Falls back to refreshing both filters if the backend didn't send
        // a specific list (older deployments).
        const staleFilterIds = data.stale_filter_ids?.length
          ? data.stale_filter_ids
          : [filterIdA, filterIdB];
        queryClient.invalidateQueries({ queryKey: ['ra-analysis-schema-status'] });
        setOutdatedRefresh({ staleFilterIds, filterIdA, filterIdB });
      } else if (status === 404) {
        toast.error(lang.get('msg.comparisonRequiresAnalysis'));
      } else {
        // Surface the actual error message from the async runner (carried
        // on the polling-error throw at comparison.ts:167) when available —
        // otherwise the user only sees the generic "try again" copy and
        // can't act on the real cause.
        const detail = (error as Error)?.message;
        toast.error(
          detail && detail !== 'Comparison failed' ? detail : lang.get('msg.comparisonFailed'),
        );
      }
    }
  };

  // Poll the per-filter schema-status until each affected filter reports
  // current: true. Used after we kick off force re-runs so we can auto-retry
  // the comparison the moment the analyzer finishes (rather than asking the
  // user to come back manually). Cancels naturally when the component
  // unmounts via the AbortSignal.
  const waitForSchemaCurrent = async (filterId: string, signal: AbortSignal) => {
    const POLL_INTERVAL_MS = 5000;
    const MAX_WAIT_MS = 20 * 60 * 1000; // 20 minutes per filter
    const startedAt = Date.now();
    while (Date.now() - startedAt < MAX_WAIT_MS) {
      if (signal.aborted) throw new Error('Refresh cancelled');
      // eslint-disable-next-line no-await-in-loop
      await new Promise<void>((resolve) => {
        setTimeout(resolve, POLL_INTERVAL_MS);
      });
      if (signal.aborted) throw new Error('Refresh cancelled');
      // eslint-disable-next-line no-await-in-loop
      const { data } = await axiosInstanceAnalyze.get(
        API_ENDPOINTS.RESEARCH_ASSISTANT.ANALYSIS_SCHEMA_STATUS(projectId!, filterId),
        { signal },
      );
      if (data?.current) return;
    }
    throw new Error('Refresh timed out');
  };

  const refreshAbortRef = useRef<AbortController | null>(null);

  const handleRefreshStaleFilters = async () => {
    if (!outdatedRefresh) return;
    const { staleFilterIds, filterIdA, filterIdB } = outdatedRefresh;

    // Tear down any prior in-flight refresh poller before starting a new one.
    refreshAbortRef.current?.abort();
    const controller = new AbortController();
    refreshAbortRef.current = controller;

    try {
      for (let i = 0; i < staleFilterIds.length; i += 1) {
        const filterId = staleFilterIds[i];
        const label = filters?.find((f) => f._id === filterId)?.name ?? filterId;
        setRefreshStatus(
          `${lang.get('msg.refreshingAnalysisFor')} ${label} (${i + 1}/${staleFilterIds.length})…`,
        );
        // Kick off the force re-run; the POST returns once the runner has
        // accepted the job, not when analysis is done. We then poll the
        // schema-status endpoint to detect completion.
        // eslint-disable-next-line no-await-in-loop
        await reRunAnalyze({ project_id: projectId, f_id: filterId, force: true });
        // eslint-disable-next-line no-await-in-loop
        await waitForSchemaCurrent(filterId, controller.signal);
      }

      // Re-attempt the original comparison now that both segments have the
      // rich-schema overall_pages the backend requires.
      queryClient.invalidateQueries({ queryKey: ['ra-analysis-schema-status'] });
      setRefreshStatus('');
      setOutdatedRefresh(null);
      await handleCompare(filterIdA, filterIdB);
    } catch (err) {
      const aborted = (err as Error)?.message === 'Refresh cancelled';
      setRefreshStatus('');
      if (!aborted) {
        toast.error(lang.get('msg.analysisRefreshFailed'));
      }
    } finally {
      if (refreshAbortRef.current === controller) refreshAbortRef.current = null;
    }
  };

  const handleViewComparison = (comparisonId: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(SearchParam.COMPARISON_ID, comparisonId);
      return next;
    });
  };

  // Loading state while filters and comparisons load
  if (isLoadingFilters || isLoadingComparisons) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
        </div>
      </div>
    );
  }

  // Inline progress during generation
  if (isComparing || generatingProgress) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="flex flex-col items-center justify-center gap-6 py-16 rounded-xl border border-neutral-200 bg-white">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <div className="text-center">
            <p className="text-sm font-medium text-neutral-700">
              {generatingProgress || lang.get('msg.comparisonGenerating')}
            </p>
            {comparisonStartedAt && <ElapsedTimer startedAt={comparisonStartedAt} />}
          </div>
          <button
            type="button"
            onClick={() => {
              abortComparison();
              setGeneratingProgress('');
              setComparisonStartedAt(null);
            }}
            className="text-xs text-neutral-400 hover:text-neutral-600 transition-colors"
          >
            {lang.get('msg.cancel')}
          </button>
        </div>
      </div>
    );
  }

  // Inline progress while we refresh stale per-filter analyses before
  // retrying the comparison. The handler kicks off force re-runs sequentially
  // and polls schema-status until each filter reports current.
  if (isRefreshing) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="flex flex-col items-center justify-center gap-6 py-16 rounded-xl border border-neutral-200 bg-white">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <div className="text-center px-6">
            <p className="text-sm font-medium text-neutral-700">{refreshStatus}</p>
            <p className="text-xs text-neutral-400 mt-1">
              {lang.get('msg.refreshingAnalysisHint')}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              refreshAbortRef.current?.abort();
            }}
            className="text-xs text-neutral-400 hover:text-neutral-600 transition-colors"
          >
            {lang.get('msg.cancel')}
          </button>
        </div>
      </div>
    );
  }

  // Inline action card when a comparison attempt failed because one or both
  // segments have stale overall_pages. Shows which filters need refreshing
  // and offers a single click to refresh + auto-retry, replacing the previous
  // dead-end "Comparison failed" toast.
  if (outdatedRefresh) {
    const staleLabels = outdatedRefresh.staleFilterIds.map(
      (id) => filters?.find((f) => f._id === id)?.name ?? id,
    );
    return (
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="flex flex-col items-start gap-4 p-6 rounded-xl border border-amber-200 bg-amber-50">
          <div className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-amber-600 shrink-0" />
            <h3 className="text-base font-semibold text-neutral-900">
              {lang.get('msg.refreshAnalysisRequired')}
            </h3>
          </div>
          <p className="text-sm text-neutral-700">
            {lang.get('msg.refreshAnalysisRequiredDescription')}
          </p>
          <ul className="text-sm text-neutral-600 list-disc pl-5">
            {staleLabels.map((label) => (
              <li key={label}>{label}</li>
            ))}
          </ul>
          <div className="flex items-center gap-3 pt-2">
            <Button variant="default" onClick={handleRefreshStaleFilters}>
              {staleLabels.length === 1
                ? lang.get('msg.refreshAnalysisCta')
                : `${lang.get('msg.refreshAnalysisCta')} (${staleLabels.length})`}
            </Button>
            <button
              type="button"
              onClick={() => setOutdatedRefresh(null)}
              className="text-xs text-neutral-500 hover:text-neutral-700 transition-colors"
            >
              {lang.get('msg.cancel')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Empty state: not enough filters
  if (!hasEnoughFilters) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
          <div className="flex items-center justify-center h-12 w-12 rounded-full bg-neutral-100">
            <Filter className="h-6 w-6 text-neutral-400" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-neutral-900 mb-1">
              {lang.get('msg.createAudienceFiltersFirst')}
            </h3>
            <p className="text-sm text-neutral-500 max-w-md mb-4">
              {lang.get('msg.createAudienceFiltersFirstDescription')}
            </p>
          </div>
          <Button onClick={() => setShowCreateFilterModal(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            {lang.get('msg.addFilter')}
          </Button>
        </div>

        {showCreateFilterModal && (
          <EditFilterModal
            project_id={projectId}
            setOpenEditModal={setShowCreateFilterModal}
            // onReRun is optional on this component's props but required by
            // EditFilterModal. Fall back to a no-op promise rather than a
            // non-null assertion — if the prop is ever omitted the modal
            // just can't trigger a re-run, instead of crashing at runtime
            // when EditFilterModal tries to call undefined().
            onReRun={onReRun ?? (() => Promise.resolve())}
            editFilterData={null}
            refetch={refetchFilters}
          />
        )}
      </div>
    );
  }

  // Empty state: no comparisons yet
  if (!isLoadingComparisons && (!comparisons || comparisons.length === 0)) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
          <div className="flex items-center justify-center h-12 w-12 rounded-full bg-emerald-50">
            <ArrowLeftRight className="h-6 w-6 text-emerald-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-neutral-900 mb-1">
              {lang.get('msg.compareAudienceSegments')}
            </h3>
            <p className="text-sm text-neutral-500 max-w-md mb-4">
              {lang.get('msg.compareAudienceSegmentsDescription')}
            </p>
          </div>
          <Button
            onClick={() => setShowCompareModal(true)}
            disabled={isRerunLoading}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            {lang.get('msg.newComparison')}
          </Button>
        </div>

        {showCompareModal && (
          <CompareFiltersModal
            filters={filters || []}
            isOpen={showCompareModal}
            onClose={() => setShowCompareModal(false)}
            onCompare={handleCompare}
            isLoading={isComparing}
            analysisLanguage={analysisLanguage}
          />
        )}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-neutral-900">
            {lang.get('msg.compareSegments')}
          </h2>
          <p className="text-sm text-neutral-500 mt-0.5">
            {lang.get('msg.compareSegmentsDescription')}
          </p>
        </div>
        <Button
          onClick={() => setShowCompareModal(true)}
          disabled={isRerunLoading}
          size="sm"
          className="gap-2"
        >
          <Plus className="h-4 w-4" />
          {lang.get('msg.newComparison')}
        </Button>
      </div>

      {/* Comparison list */}
      <div className="flex flex-col gap-3">
        {comparisons?.map((comparison) => (
          <ComparisonCard
            key={comparison._id}
            comparison={comparison}
            onView={() => handleViewComparison(comparison._id)}
            onRegenerate={handleCompare}
            isRegenerating={isComparing}
          />
        ))}
      </div>

      {showCompareModal && (
        <CompareFiltersModal
          filters={filters || []}
          isOpen={showCompareModal}
          onClose={() => setShowCompareModal(false)}
          onCompare={handleCompare}
          isLoading={isComparing}
          analysisLanguage={analysisLanguage}
        />
      )}
    </div>
  );
}

export default ComparisonHub;
